/**
 * TransitionTimeline — chronological list of transitions for a single
 * (site, device, condition) identity. Source: GET /:siteId/alarms/:deviceId/:conditionKey.
 *
 * Contract invariant (load-bearing): all 7 transition values render with
 * DISTINCT visual treatment. Never collapse underlying_clear into cleared;
 * never merge communication_loss / communication_restored with functional
 * transitions. The overhaul's entire lifecycle model depends on this.
 */

import type { AlarmHistoryEvent, Transition } from '../../types/alarm.types';

/** Each transition maps to a unique text color — 7 distinct classes. */
const TRANSITION_STYLES: Record<Transition, string> = {
  raised: 'text-ems-red font-semibold',
  cleared: 'text-ems-green font-semibold',
  underlying_clear: 'text-ems-amber',
  acknowledged: 'text-ems-green-bright',
  state_change: 'text-ems-cyan',
  communication_loss: 'text-ems-text-muted italic',
  communication_restored: 'text-ems-blue',
};

/** Short glyph by transition — helps visual scanning in dense timelines. */
const TRANSITION_GLYPH: Record<Transition, string> = {
  raised: '▲',
  cleared: '▼',
  underlying_clear: '◇',
  acknowledged: '✓',
  state_change: '↔',
  communication_loss: '⚠',
  communication_restored: '⟲',
};

export function TransitionTimeline({ events }: { events: AlarmHistoryEvent[] }) {
  if (events.length === 0) {
    return (
      <div className="bg-ems-bg-card border border-ems-border rounded p-3 text-xs text-ems-text-muted italic">
        No transition history.
      </div>
    );
  }

  return (
    <ul className="bg-ems-bg-card border border-ems-border rounded p-3 text-xs text-ems-text-muted">
      {events.map((e) => (
        <li
          key={e.event_id}
          className="flex gap-3 py-1 pl-3 border-l-2 border-ems-green/30 ml-1 items-start"
        >
          <span className="font-mono text-ems-text-muted min-w-[160px]">
            {new Date(e.ts).toLocaleString()}
          </span>
          <span
            className={`${TRANSITION_STYLES[e.transition]} min-w-[180px]`}
            data-transition={e.transition}
          >
            <span className="mr-1.5" aria-hidden="true">
              {TRANSITION_GLYPH[e.transition]}
            </span>
            {e.transition}
          </span>
          {e.value !== null && e.value !== undefined && (
            <span>
              value: <code className="text-ems-text-secondary">{e.value}</code>
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}
